import type { AbsenceBalanceChangeInput } from './model';
import { dateError, numberError } from './absenceFormValues';

export type AdminAbsenceBalanceAction = 'GRANT' | 'INCREASE' | 'DECREASE';
export type AdminAbsenceBalanceDraft = { action: AdminAbsenceBalanceAction; quantity: string; effectiveOn: string; reason: string };
export type AdminAbsenceBalanceChange = { operation: 'GRANT' | 'ADJUSTMENT'; input: AbsenceBalanceChangeInput };

export function adminAbsenceBalanceErrors(draft: AdminAbsenceBalanceDraft) {
  return {
    quantity: numberError(draft.quantity.trim(), { positive: true, precision: 4 }),
    effectiveOn: dateError(draft.effectiveOn),
    reason: draft.reason.trim() ? undefined : '변경 사유를 입력해 주세요.',
  };
}

export function adminAbsenceBalanceChange(draft: AdminAbsenceBalanceDraft): AdminAbsenceBalanceChange | undefined {
  if (Object.values(adminAbsenceBalanceErrors(draft)).some(Boolean)) return undefined;
  return { operation: draft.action === 'GRANT' ? 'GRANT' : 'ADJUSTMENT', input: { quantity: adminAbsenceBalanceDelta(draft.action, draft.quantity)!, effectiveOn: draft.effectiveOn, reason: draft.reason.trim() } };
}

/** 사유 입력 전에도 수량의 효과를 미리 보여줄 수 있습니다. 저장 검증은 전체 초안을 검사합니다. */
export function adminAbsenceBalanceDelta(action: AdminAbsenceBalanceAction, value: string): string | undefined {
  if (numberError(value.trim(), { positive: true, precision: 4 })) return undefined;
  const [integer, fraction] = value.trim().split('.');
  const decimal = fraction?.replace(/0+$/, '');
  const quantity = `${integer.replace(/^0+(?=\d)/, '')}${decimal ? `.${decimal}` : ''}`;
  return `${action === 'DECREASE' ? '-' : ''}${quantity}`;
}

/** 원장 numeric(12,4) 수량을 이진 소수 오차 없이 합산합니다. 미확인 값은 0으로 간주하지 않습니다. */
export function addAbsenceBalanceQuantity(value: string, delta: string): string | undefined {
  const scaled = (raw: string): bigint | undefined => {
    if (!/^-?\d+(?:\.\d{1,4})?$/.test(raw)) return undefined;
    const [integer, fraction = ''] = raw.replace(/^-/, '').split('.');
    return (BigInt(integer) * 10000n + BigInt(fraction.padEnd(4, '0'))) * (raw.startsWith('-') ? -1n : 1n);
  };
  const before = scaled(value);
  const change = scaled(delta);
  if (before === undefined || change === undefined) return undefined;
  const total = before + change;
  const absolute = total < 0n ? -total : total;
  const fraction = (absolute % 10000n).toString().padStart(4, '0').replace(/0+$/, '');
  return `${total < 0n ? '-' : ''}${absolute / 10000n}${fraction ? `.${fraction}` : ''}`;
}

export function formatAbsenceBalanceQuantity(value: string): string {
  if (!/^-?\d+(?:\.\d+)?$/.test(value)) return '확인 필요';
  const [integer, rawFraction] = value.split('.');
  const fraction = rawFraction?.replace(/0+$/, '');
  return `${integer.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}${fraction ? `.${fraction}` : ''}`;
}
